import { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import App from "App";
import "locale";
import reportWebVitals from "reportWebVitals";
import store from "store/reducer";
import ProtectedRoute from "components/protected-route";
import PublicRoute from "components/public-route";
import ErrorBoundary from "components/error-boundary";
import { lazyWithRetry } from "utils/lazy-with-retry";
import { AlertProvider } from "utils/alerts";
import AnalyticsTracker from "components/analytics-tracker";
import routes from "./routes";
import ReactGA from "react-ga";

// Component Imports
const Loading = lazyWithRetry(() => import("./components/loading"));

// Initialize Google Analytics
const TRACKING_ID = process.env.REACT_APP_GTM_ID || "UA-000000-01"; // Fallback ID to prevent errors if env is missing
ReactGA.initialize(TRACKING_ID);

console.log("DEBUG: routes.adminLayout =", routes.adminLayout);
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <ErrorBoundary>
    <Suspense fallback={<Loading />}>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Provider store={store}>
          <AnalyticsTracker />
          <AlertProvider>
            <Routes>
              {/* Admin routes - rendered outside App component */}
              {routes.adminLayout && renderRoutes(routes.adminLayout, "admin")}
              
              {/* Main app routes - wrapped in App component for non-admin paths */}
              <Route path="/*" element={<App><Routes>{renderRoutes(routes.blankLayout, "blank")}{renderRoutes(routes.defaultLayout, "default")}</Routes></App>} />
            </Routes>
          </AlertProvider>
        </Provider>
      </Router>
    </Suspense>
  </ErrorBoundary>
);

// Generic renderer for any route list
function renderRoutes(routesArray = [], prefix = "route") {
  return routesArray.map(
    ({ path, element: Element, layout, isProtected, wrapper: Wrapper }, index) => {
      let routeElement;
      
      if (Wrapper) {
        // Admin routes with wrapper
        routeElement = (
          <Wrapper>
            <Element />
          </Wrapper>
        );
      } else if (isProtected) {
        // Regular protected routes
        routeElement = (
          <ProtectedRoute layout={layout}>
            <Element />
          </ProtectedRoute>
        );
      } else {
        // Public routes
        routeElement = (
          <PublicRoute layout={layout}>
            <Element />
          </PublicRoute>
        );
      }

      return (
        <Route
          key={`${prefix}-${index}`}
          path={path}
          element={routeElement}
        />
      );
    }
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
